import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Phone } from "lucide-react";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import CTABlock from "@/components/sections/CTABlock";
import teamMember2 from "@/assets/team-member-2.jpg";
import heroImage from "@/assets/bg-exterior-1.jpg";

const InstagramIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3.5" y="3.5" width="17" height="17" rx="4.5" />
    <circle cx="12" cy="12" r="3.75" />
    <circle cx="17.5" cy="6.5" r="0.75" fill="currentColor" stroke="none" />
  </svg>
);

const TeamJessiree = () => {
  useEffect(() => {
    document.title = "Jessiree Peña | Hadassah Homes";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Meet Jessiree Peña, Realtor at Hadassah Homes. A client-first agent with a background in social work bringing empathy, clarity, and care to every transaction.");
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = "https://hadassahhomesinc.com/team/jessiree-pena";
  }, []);

  return (
    <SiteLayout>
      <PageHero
        compact
        eyebrow="Meet the Team"
        title="Jessiree Peña"
        backgroundImage={heroImage}
      />

      <ContentSection>
        <div className="flex flex-col md:flex-row gap-12 md:gap-20 items-start">
          <div className="w-full md:w-5/12 shrink-0">
            <div className="aspect-[3/4] overflow-hidden">
              <img
                src={teamMember2}
                alt="Jessiree Peña, Realtor at Hadassah Homes"
                className="w-full h-full object-cover object-top"
                loading="eager"
              />
            </div>
            <div className="mt-6 space-y-3 border-t border-border pt-6">
              <p className="font-body text-[10px] uppercase tracking-[0.3em] text-accent">Contact</p>
              <a href="mailto:jessi@hadassahhomesinc.com" className="flex items-center gap-3 font-body text-sm text-foreground hover:text-accent transition-colors">
                <Mail className="w-4 h-4 text-accent shrink-0" strokeWidth={1.5} />
                jessi@hadassahhomesinc.com
              </a>
              <a href="tel:+17815217670" className="flex items-center gap-3 font-body text-sm text-muted-foreground hover:text-accent transition-colors">
                <Phone className="w-4 h-4 text-accent shrink-0" strokeWidth={1.5} />
                (781) 521-7670
              </a>
              <div className="pt-2 space-y-2">
                <p className="font-body text-[10px] uppercase tracking-[0.3em] text-accent/80">Social</p>
                <div className="flex flex-wrap gap-2">
                  <a
                    href="https://www.instagram.com/realestatewithjessi/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Jessiree Peña on Instagram"
                    title="Instagram"
                    className="inline-flex h-8 w-8 items-center justify-center border border-border/60 text-muted-foreground transition-colors hover:border-accent hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                  >
                    <InstagramIcon />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full space-y-6">
            <span className="font-body text-[10px] uppercase tracking-[0.4em] text-accent">Team</span>
            <h2 className="font-headline text-3xl md:text-5xl text-foreground leading-tight">
              Realtor
            </h2>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Jessiree brings a unique blend of experience in social work, psychology, and healthcare administration to her real estate career.
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              With a background focused on ensuring positive patient experiences and advocating for individual needs, she approaches every real estate transaction with empathy, attention to detail, and strong communication.
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Jessiree is committed to helping clients navigate buying, selling, and investing with confidence while providing a supportive and personalized experience.
            </p>
            <div className="pt-4 flex flex-wrap gap-4">
              <Link
                to="/consultation"
                className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.1em] px-8 py-3.5 bg-accent text-white hover:bg-accent/90 transition-colors"
              >
                Book with Jessiree <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/team"
                className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.1em] px-8 py-3.5 border border-border text-foreground hover:border-accent hover:text-accent transition-colors"
              >
                Meet the Team
              </Link>
            </div>
          </div>
        </div>
      </ContentSection>

      <CTABlock
        variant="dark"
        title="Ready to Work With Jessiree?"
        description="Get compassionate, strategy-driven guidance for buying or selling your home."
        primaryCta={{ text: "Book a Consultation →", href: "/consultation" }}
      />
    </SiteLayout>
  );
};

export default TeamJessiree;
